import type { AxiosResponse } from "axios";
import { ZodError } from "zod";
import type z from "zod";

import { reportError } from "utils/errorReporting";

type Schema = z.ZodType<unknown>;

/**
 * Validate the data response against the associated zod schema.
 *
 * @param response Response to validate
 * @param schema The schema to check agains
 * @param throwErrorWhenFailing If validating fails an error should be thrown
 * @param strict If true, reject responses with additional properties not in schema
 *
 * @returns the validated data OR the unvalidated data only type casted
 */
export const validateResponse = <T extends Schema>(
  response: AxiosResponse,
  schema: T,
  throwErrorWhenFailing = false,
  strict = false
): z.infer<T> => {
  const { data, config } = response;
  const validationSchema =
    strict && typeof schema === "object" && schema !== null && "strict" in schema
      ? ((schema as unknown as z.ZodObject<z.ZodRawShape>).strict() as unknown as T)
      : schema;
  const result = validationSchema.safeParse(data);
  if (result.success) {
    return result.data as z.infer<T>;
  }

  const { error } = result;
  const cleanedUrl = config?.url?.replace(/\d+/g, "{id}")?.replace(/\?.*$/, "");

  const filteredIssues = error.issues.filter(
    issue => !(issue.code === "invalid_value" && typeof issue.input === "string" && issue.input.includes("SCRUBBED"))
  );

  if (filteredIssues.length === 0) {
    return data as z.infer<T>;
  }

  const reportableError = new ZodError(filteredIssues);

  reportError(`DataValidation error in ${cleanedUrl}`, reportableError);

  // New endpoints will usually want to throw errors instead of returning faulty data
  if (throwErrorWhenFailing) throw reportableError;

  /**
   * If we end up here the data is corrupt or our Zod schemas are faulty.
   * The desired behaviour is to throw an error when that happens.
   *
   * We decided to slowly transition into this data validation without crashing the app at this stage.
   * That is why we also return the "unvalidated" data. I.e. same behaviour as without any validation.
   */
  return data as z.infer<T>;
};
